import { type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, CircleDot, Target, TrendingUp } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { Card } from '@/components/ui/card';
import type { ForkHireAnalyticsSummary } from '@/hooks/useForkHireAnalytics';
import {
  getForkHireAnalyticsChartTotals,
  getForkHireAnalyticsPercentage,
} from '@/lib/forkhire/analyticsMetrics';

type DonutDatum = {
  key: string;
  label: string;
  value: number;
  color: string;
};

type ChartCardHeadingProps = {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
};

type DonutChartCardProps = ChartCardHeadingProps & {
  data: DonutDatum[];
  centerValue: string;
  centerLabel: string;
  accessibleSummary: string;
  emptyMessage: string;
};

const EMPTY_CHART_COLOR = 'hsl(var(--muted))';

function ChartCardHeading({ icon: Icon, title, subtitle }: ChartCardHeadingProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <h3 className="font-display text-base font-semibold text-foreground sm:text-lg">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground sm:text-sm">{subtitle}</p>
      </div>
    </div>
  );
}

function DonutChartCard({
  icon,
  title,
  subtitle,
  data,
  centerValue,
  centerLabel,
  accessibleSummary,
  emptyMessage,
}: DonutChartCardProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const chartData = total > 0
    ? data
    : [{ key: 'empty', label: emptyMessage, value: 1, color: EMPTY_CHART_COLOR }];

  return (
    <Card className="flex min-w-0 flex-col rounded-xl border-border p-5 shadow-card sm:p-6">
      <ChartCardHeading icon={icon} title={title} subtitle={subtitle} />

      <div
        className="relative mx-auto mt-4 h-52 w-full max-w-[17rem]"
        role="img"
        aria-label={accessibleSummary}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={84}
              paddingAngle={total > 0 && data.filter((item) => item.value > 0).length > 1 ? 3 : 0}
              stroke="hsl(var(--card))"
              strokeWidth={3}
              isAnimationActive={false}
            >
              {chartData.map((item) => (
                <Cell key={item.key} fill={item.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-2xl font-bold tabular-nums text-foreground" dir="ltr">{centerValue}</span>
          <span className="mt-1 max-w-24 text-[11px] leading-4 text-muted-foreground">{centerLabel}</span>
        </div>
      </div>

      {total > 0 ? (
        <ul className="mt-auto grid gap-2" aria-label={title}>
          {data.map((item) => (
            <li key={item.key} className="flex min-w-0 items-center justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm border border-foreground/10"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
                <span className="min-w-0 leading-5">{item.label}</span>
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-foreground" dir="ltr">{item.value}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-auto rounded-lg bg-muted/60 px-3 py-2 text-center text-xs text-muted-foreground">
          {emptyMessage}
        </p>
      )}

      <span className="sr-only">{accessibleSummary}</span>
    </Card>
  );
}

export function ForkHirePerformanceCharts({ summary }: { summary: ForkHireAnalyticsSummary }) {
  const { t } = useTranslation();
  const totals = getForkHireAnalyticsChartTotals(summary);
  const scoredPercentage = getForkHireAnalyticsPercentage(totals.scored, totals.applications);
  const interviewedPercentage = getForkHireAnalyticsPercentage(totals.interviewed, totals.applications);
  const isEmpty = totals.applications === 0;

  const pipelineStages = [
    {
      key: 'applications',
      label: t('hire.analytics.applications'),
      value: totals.applications,
      percentage: getForkHireAnalyticsPercentage(totals.applications, totals.applications),
      color: 'hsl(var(--primary))',
    },
    {
      key: 'scored',
      label: t('hire.analytics.scored'),
      value: totals.scored,
      percentage: scoredPercentage,
      color: 'hsl(var(--info))',
    },
    {
      key: 'interviewed',
      label: t('hire.analytics.interviewed'),
      value: totals.interviewed,
      percentage: interviewedPercentage,
      color: 'hsl(var(--brand-violet))',
    },
    {
      key: 'hired',
      label: t('hire.analytics.hired'),
      value: totals.hired,
      percentage: getForkHireAnalyticsPercentage(totals.hired, totals.applications),
      color: 'hsl(var(--success))',
    },
  ];

  const pipelineSummary = t('hire.analytics.charts.pipeline.summary', {
    applications: totals.applications,
    scored: totals.scored,
    interviewed: totals.interviewed,
    hired: totals.hired,
  });
  const processingSummary = t('hire.analytics.charts.processing.summary', {
    scored: totals.scored,
    notScored: totals.notScored,
    total: totals.applications,
    percentage: scoredPercentage,
  });
  const interviewSummary = t('hire.analytics.charts.interviews.summary', {
    interviewed: totals.interviewed,
    notInterviewed: totals.notInterviewed,
    total: totals.applications,
    percentage: interviewedPercentage,
  });
  const outcomesSummary = t('hire.analytics.charts.outcomes.summary', {
    hired: totals.hired,
    rejected: totals.rejected,
    unresolved: totals.unresolved,
    total: totals.applications,
  });

  return (
    <section aria-labelledby="organization-performance-title">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Activity className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <h2 id="organization-performance-title" className="font-display text-lg font-semibold text-foreground sm:text-xl">
            {t('hire.analytics.charts.sectionTitle')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('hire.analytics.charts.sectionSubtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="flex min-w-0 flex-col rounded-xl border-border p-5 shadow-card sm:p-6">
          <ChartCardHeading
            icon={TrendingUp}
            title={t('hire.analytics.charts.pipeline.title')}
            subtitle={t('hire.analytics.charts.pipeline.subtitle')}
          />

          <div className="mt-6 space-y-4" role="img" aria-label={pipelineSummary}>
            {pipelineStages.map((stage, index) => (
              <div key={stage.key} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3">
                <div className="flex flex-col items-center" aria-hidden="true">
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold text-white"
                    style={{ backgroundColor: stage.color }}
                  >
                    {index + 1}
                  </span>
                  {index < pipelineStages.length - 1 ? <span className="mt-1 h-full min-h-5 w-px bg-border" /> : null}
                </div>
                <div className="min-w-0 pb-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground">{stage.label}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="font-semibold tabular-nums text-foreground" dir="ltr">{stage.value}</span>
                      <span className="w-11 text-end text-xs tabular-nums text-muted-foreground" dir="ltr">
                        {stage.percentage}%
                      </span>
                    </span>
                  </div>
                  <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${stage.percentage}%`, backgroundColor: stage.color }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-auto border-t border-border pt-4 text-xs leading-5 text-muted-foreground">
            {isEmpty ? t('hire.analytics.charts.empty') : t('hire.analytics.charts.pipeline.note')}
          </p>
          <span className="sr-only">{pipelineSummary}</span>
        </Card>

        <DonutChartCard
          icon={CircleDot}
          title={t('hire.analytics.charts.processing.title')}
          subtitle={t('hire.analytics.charts.processing.subtitle')}
          data={[
            {
              key: 'scored',
              label: t('hire.analytics.scored'),
              value: totals.scored,
              color: 'hsl(var(--primary))',
            },
            {
              key: 'notScored',
              label: t('hire.analytics.charts.processing.notScored'),
              value: totals.notScored,
              color: 'hsl(var(--brand-lavender))',
            },
          ]}
          centerValue={`${scoredPercentage}%`}
          centerLabel={t('hire.analytics.charts.processing.centerLabel')}
          accessibleSummary={processingSummary}
          emptyMessage={t('hire.analytics.charts.empty')}
        />

        <DonutChartCard
          icon={Target}
          title={t('hire.analytics.charts.interviews.title')}
          subtitle={t('hire.analytics.charts.interviews.subtitle')}
          data={[
            {
              key: 'interviewed',
              label: t('hire.analytics.interviewed'),
              value: totals.interviewed,
              color: 'hsl(var(--info))',
            },
            {
              key: 'notInterviewed',
              label: t('hire.analytics.charts.interviews.notInterviewed'),
              value: totals.notInterviewed,
              color: 'hsl(var(--brand-lavender))',
            },
          ]}
          centerValue={`${interviewedPercentage}%`}
          centerLabel={t('hire.analytics.charts.interviews.centerLabel')}
          accessibleSummary={interviewSummary}
          emptyMessage={t('hire.analytics.charts.empty')}
        />

        <DonutChartCard
          icon={Activity}
          title={t('hire.analytics.charts.outcomes.title')}
          subtitle={t('hire.analytics.charts.outcomes.subtitle')}
          data={[
            {
              key: 'hired',
              label: t('hire.analytics.hired'),
              value: totals.hired,
              color: 'hsl(var(--success))',
            },
            {
              key: 'rejected',
              label: t('hire.analytics.rejected'),
              value: totals.rejected,
              color: 'hsl(var(--destructive))',
            },
            {
              key: 'unresolved',
              label: t('hire.analytics.charts.outcomes.unresolved'),
              value: totals.unresolved,
              color: 'hsl(var(--primary))',
            },
          ]}
          centerValue={String(totals.applications)}
          centerLabel={t('hire.analytics.charts.outcomes.centerLabel')}
          accessibleSummary={outcomesSummary}
          emptyMessage={t('hire.analytics.charts.empty')}
        />
      </div>
    </section>
  );
}
